/**
 * @typedef {Object} StoreCategoryNode
 * @property {string|number} categoryId
 * @property {string} [categoryName]
 * @property {number} [grade]
 * @property {number} [seq]
 * @property {number} [priority]
 * @property {number} [prodNum]
 * @property {number} [prodCount]
 * @property {string|number} [prodId]
 * @property {Array<StoreCategoryNode>} [children]
 * @property {Array<StoreCategoryNode>} [categories]
 * @property {Array<StoreCategoryNode>} [products]
 */

/**
 * @description 判断节点是否为挂靠在分类树下的商品叶子
 * @param {Object|null|undefined} node
 * @returns {boolean}
 */
export function isCategoryProductNode(node) {
  if (!node || typeof node !== 'object') return false
  const prodId = node.prodId ?? node.productId ?? node.id
  if (prodId === undefined || prodId === null || String(prodId).trim() === '') return false
  return Boolean(node.prodName || node.modelNo || node.modelName)
}

/**
 * @description 读取分类子节点（兼容接口 children / 历史 categories）
 * @param {Object|null|undefined} node
 * @returns {Array<Object>}
 */
export function getCategoryChildNodes(node) {
  if (!node || typeof node !== 'object') return []
  const raw = node.children ?? node.categories ?? []
  return Array.isArray(raw) ? raw : []
}

/**
 * @description 读取分类节点上的商品数量（接口 prodCount，兼容 prodNum）
 * @param {Object|null|undefined} node
 * @returns {number}
 */
export function getCategoryProdCount(node) {
  const count = Number(node?.prodCount ?? node?.prodNum ?? 0)
  return Number.isFinite(count) ? count : 0
}

/**
 * @description 二级分类是否可展示（prodCount > 0；无 prodCount 时回退子树商品判断）
 * @param {Object|null|undefined} node
 * @returns {boolean}
 */
export function isSecondaryCategoryVisible(node) {
  if (!node || typeof node !== 'object') return false

  if (node.prodCount !== undefined && node.prodCount !== null) {
    return getCategoryProdCount(node) > 0
  }

  return categorySubtreeHasProducts(node)
}

/**
 * @description 分类子树是否包含可售商品（prodId 叶子或 prodNum / prodCount）
 * @param {Object|null|undefined} node
 * @returns {boolean}
 */
export function categorySubtreeHasProducts(node) {
  if (!node || typeof node !== 'object') return false
  if (isCategoryProductNode(node)) return true

  const prodNum = node.prodNum ?? node.prodCount
  if (prodNum !== undefined && prodNum !== null && Number(prodNum) > 0) return true

  return getCategoryChildNodes(node).some((child) => categorySubtreeHasProducts(child))
}

/**
 * @description 分类排序：优先 seq，其次 priority
 * @param {Object} a
 * @param {Object} b
 * @returns {number}
 */
export function compareCategoryOrder(a, b) {
  const seqA = Number(a?.seq ?? a?.priority ?? 0)
  const seqB = Number(b?.seq ?? b?.priority ?? 0)
  if (Number.isFinite(seqA) && Number.isFinite(seqB) && seqA !== seqB) {
    return seqA - seqB
  }
  return String(a?.categoryName || '').localeCompare(String(b?.categoryName || ''), 'zh-CN')
}

/**
 * @description 归一化单个分类节点：子分类写入 categories，完整子树写入 children
 * @param {Object|null|undefined} node
 * @returns {StoreCategoryNode|null}
 */
export function normalizeCategoryNode(node) {
  if (!node || typeof node !== 'object') return null
  if (isCategoryProductNode(node)) return { ...node }

  const rawChildren = getCategoryChildNodes(node)
  const categoryChildren = rawChildren.filter((child) => !isCategoryProductNode(child))
  const productLeaves = rawChildren.filter(isCategoryProductNode)

  const normalizedCategories = categoryChildren
    .map((child) => normalizeCategoryNode(child))
    .filter(Boolean)

  return {
    ...node,
    categories: normalizedCategories,
    children: [...normalizedCategories, ...productLeaves],
    products: productLeaves,
  }
}

/**
 * @description 过滤无商品分类，并统一输出 categories / children 结构
 * @param {Array<Object>} items
 * @returns {Array<StoreCategoryNode>}
 */
export function filterCategoryTree(items) {
  if (!Array.isArray(items)) return []

  return items
    .map((item) => {
      if (isCategoryProductNode(item)) {
        return categorySubtreeHasProducts(item) ? { ...item } : null
      }

      const rawChildren = getCategoryChildNodes(item)
      const categoryChildren = rawChildren
        .filter((child) => !isCategoryProductNode(child))
        .filter((child) => {
          if (Number(child?.grade) === 2) {
            return isSecondaryCategoryVisible(child)
          }
          return true
        })
      const productLeaves = rawChildren.filter(isCategoryProductNode)
      const filteredCategories = filterCategoryTree(categoryChildren)

      const hasProducts =
        productLeaves.length > 0 ||
        filteredCategories.length > 0 ||
        categorySubtreeHasProducts(item)

      if (!hasProducts) return null

      return {
        ...item,
        categories: filteredCategories,
        children: [...filteredCategories, ...productLeaves],
        products: productLeaves,
      }
    })
    .filter(Boolean)
    .sort(compareCategoryOrder)
}

/**
 * @description 解包分类接口响应
 * @param {unknown} data
 * @returns {Array<Object>}
 */
export function unwrapCategoryApiPayload(data) {
  if (Array.isArray(data)) return data
  if (!data || typeof data !== 'object') return []

  const record = /** @type {Record<string, unknown>} */ (data)
  if (Array.isArray(record.categoryInfo)) return record.categoryInfo
  if (Array.isArray(record.data)) return record.data
  if (Array.isArray(record.children)) return record.children
  if (Array.isArray(record.list)) return record.list

  return []
}

/**
 * @description 将 `/prod/prod/getCategoryProd` 响应转为商城统一分类树
 * @param {unknown} data
 * @returns {Array<StoreCategoryNode>}
 */
export function buildCategoryTreeFromApi(data) {
  const rawList = unwrapCategoryApiPayload(data)
  return filterCategoryTree(rawList)
}

/**
 * @description 取一级分类下的二级分类列表（仅 prodCount > 0）
 * @param {Object|null|undefined} primaryCategory
 * @returns {Array<StoreCategoryNode>}
 */
export function getSecondaryCategories(primaryCategory) {
  if (!primaryCategory || typeof primaryCategory !== 'object') return []

  const list = Array.isArray(primaryCategory.categories)
    ? primaryCategory.categories
    : getCategoryChildNodes(primaryCategory).filter((child) => !isCategoryProductNode(child))

  if (!Array.isArray(list)) return []

  return list.filter(isSecondaryCategoryVisible).sort(compareCategoryOrder)
}

/**
 * @description 汇总所有一级分类下可展示的二级分类（首页分类导航等场景）
 * @param {Array<Object>} categoryList
 * @returns {Array<StoreCategoryNode & { parentCategoryId: string|number }>}
 */
export function getAllSecondaryCategories(categoryList) {
  const primaries = Array.isArray(categoryList) ? categoryList : []

  return primaries.flatMap((primary) =>
    getSecondaryCategories(primary).map((secondary) => ({
      ...secondary,
      parentCategoryId: primary.categoryId,
    }))
  )
}
